import { SmbAttachmentStatus, type Transaction } from "@/apis/gql/types";
import { SelectCell } from "@/components/shared/cells/select";
import { SelectHeader } from "@/components/shared/select-inputs/select";
import { DataTableColumnHeader } from "@/components/shared/tables/ColumnHeader";
import { useApproveSmbTransactionAttachments } from "@/queries/transactions/approve-smb-transaction-attachments";
import { useUpdateBankTransaction } from "@/queries/transactions/update-bank-transaction";
import type { ColumnDef } from "@tanstack/react-table";
import { isWithinInterval } from "date-fns";
import { useTranslation } from "react-i18next";
import { TransactionCell } from "../../shared/transactions-todos/cells/transaction-cell";
import { InvoiceCell } from "./cells/invoice-cell";

const selectColumn: ColumnDef<Transaction> = {
  id: "select",
  size: 45,
  header: (header) => <SelectHeader {...header} />,
  cell: (cell) => (
    <div className="flex items-center justify-center h-10 w-full">
      <SelectCell {...cell} />
    </div>
  ),
  enableSorting: false,
  enableHiding: false,
};

const transactionColumn: ColumnDef<Transaction> = {
  id: "bookingDate",
  accessorKey: "transaction",
  minSize: 400,
  header: (header) => {
    const { t } = useTranslation();

    return (
      <DataTableColumnHeader
        column={header.column}
        title={t("transactions.column_header")}
      />
    );
  },
  filterFn: (row, columnId, value) => {
    if (!value) return true;

    return isWithinInterval(new Date(row.original.bookingDate), {
      start: value.from,
      end: value.to,
    });
  },
  sortingFn: (rowA, rowB) => {
    const bookedDateA = new Date(rowA.original.bookingDate);
    const bookedDateB = new Date(rowB.original.bookingDate);

    return bookedDateA.getTime() - bookedDateB.getTime();
  },
  cell: (cell) => <TransactionCell transaction={cell.row.original} />,
};

const createInvoiceColumn: ColumnDef<Transaction> = {
  id: "invoice",
  accessorKey: "invoice",
  header: (header) => {
    const { t } = useTranslation();

    return (
      <DataTableColumnHeader column={header.column} title={t("invoice")} />
    );
  },
  cell: (cell) => {
    const { table } = cell;
    const exitingRowsIds = { [cell.row.id]: true };

    const [updateBankTransaction] = useUpdateBankTransaction();
    const [approveSmbTransactionAttachments] =
      useApproveSmbTransactionAttachments();

    const handleMarkTransactionAsReviewed = async () => {
      table.setExitingRow(() => exitingRowsIds);

      await new Promise((resolve) => setTimeout(resolve, 300));

      await approveSmbTransactionAttachments({
        bankTransactionIds: [cell.row.original.bankTxnId],
        smbApproved: true,
      });

      await new Promise((resolve) => setTimeout(resolve, 100));

      table.setExitingRow(() => ({}));
    };

    const handleMarkTransactionAsLost = async () => {
      table.setExitingRow(() => exitingRowsIds);

      await new Promise((resolve) => setTimeout(resolve, 300));

      await updateBankTransaction({
        bankTransactionIds: [cell.row.original.bankTxnId],
        updatedFields: {
          smbAttachmentStatus: SmbAttachmentStatus.LOST,
        },
      });

      await new Promise((resolve) => setTimeout(resolve, 100));

      table.setExitingRow(() => ({}));
    };

    return (
      <InvoiceCell
        row={cell.row}
        transaction={cell.row.original}
        table={cell.table}
        onMarkAsReviewed={handleMarkTransactionAsReviewed}
        onMarkAsLost={handleMarkTransactionAsLost}
      />
    );
  },
  enableSorting: false,
};

const counterpartyNameColumn: ColumnDef<Transaction> = {
  id: "counterpartyName",
  accessorKey: "counterpartyName",
};

const purposeColumn: ColumnDef<Transaction> = {
  id: "purpose",
  accessorKey: "purpose",
};

const amountColumn: ColumnDef<Transaction> = {
  id: "amount",
  accessorKey: "amount",
  filterFn: (row, id, filterValue) => {
    if (!filterValue) return true;

    const transaction = row.original;
    const rawAmount = transaction.amount;

    const { min, max, mode } = filterValue;

    if (mode === "single" && min !== undefined) {
      const rowAmountStr = Math.abs(rawAmount).toString();
      const minStr = Math.abs(min).toString();

      if (rawAmount === min) {
        return true;
      }

      return rowAmountStr.includes(minStr);
    }

    if (mode === "range") {
      if (min !== undefined && max !== undefined) {
        return rawAmount >= min && rawAmount <= max;
      }

      if (min !== undefined && max === undefined) {
        return rawAmount >= min;
      }

      if (max !== undefined && min === undefined) {
        return rawAmount <= max;
      }
    }

    return true;
  },
};

const bankConnectionColumn: ColumnDef<Transaction> = {
  id: "bankConnection",
  accessorKey: "bankConnection.bankConnectionId",
  filterFn: (row, id, filterValue) => {
    const bankConnectionId = row.original.bankConnection.bankConnectionId;

    if (!filterValue || filterValue.length === 0) {
      return true;
    }

    return bankConnectionId.includes(filterValue);
  },
};

const hasAttachmentsColumn: ColumnDef<Transaction> = {
  id: "hasAttachments",
  accessorKey: "attachments",
  filterFn: (row, id, filterValue) => {
    const transaction = row.original;

    if (
      transaction.smbAttachmentStatus === SmbAttachmentStatus.ATTACHED ||
      transaction.smbAttachmentStatus === SmbAttachmentStatus.LOST
    ) {
      return false;
    }

    return true;
  },
};

const smbStatusColumn: ColumnDef<Transaction> = {
  id: "smbAttachmentStatus",
  accessorKey: "smbAttachmentStatus",
  filterFn: (row, id, filterValue) => {
    const transaction = row.original;

    if (!filterValue) return true;

    return filterValue.includes(transaction.smbAttachmentStatus);
  },
};

const sideColumn: ColumnDef<Transaction> = {
  id: "side",
  accessorKey: "side",
};

export const columns: ColumnDef<Transaction>[] = [
  selectColumn,
  transactionColumn,
  createInvoiceColumn,
  counterpartyNameColumn,
  purposeColumn,
  amountColumn,
  bankConnectionColumn,
  hasAttachmentsColumn,
  smbStatusColumn,
  sideColumn,
];
